import { lazy, Suspense } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SkipToContent from "@/components/SkipToContent";
import ZoomBookingListener from "@/components/ZoomBookingListener";

// Lazy load non-critical widgets - these are not needed for initial render
const ExitIntentPopup = lazy(() => import("@/components/ExitIntentPopup"));
const FloatingCallButton = lazy(() => import("@/components/FloatingCallButton"));
const AIChatWidget = lazy(() => import("@/components/AIChatWidget"));
const CalendarSection = lazy(() => import("@/components/CalendarSection"));
const MultiPlatformReviews = lazy(() => import("@/components/MultiPlatformReviews"));
const SolarEstimateCard = lazy(() => import("@/components/SolarEstimateCard"));
const NeighborhoodActivity = lazy(() => import("@/components/NeighborhoodActivity"));


interface LayoutProps {
  children: React.ReactNode;
  showWidgets?: boolean;
  showCalendar?: boolean;
  showSolarEstimate?: boolean;
}

const Layout = ({ children, showWidgets = true, showCalendar = true, showSolarEstimate = true }: LayoutProps) => {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Accessibility: Skip to main content link */}
      <SkipToContent />
      <ZoomBookingListener />
      
      {/* Header with navigation landmark */}
      <Header />
      
      {/* Main content area with proper landmark and ID for skip link */}
      <main id="main-content" role="main" className="flex-1" tabIndex={-1}>
        {children}
      </main>
      
      {/* Calendar & Reviews - shown on all public pages */}
      {showCalendar && (
        <Suspense fallback={<div className="min-h-[600px] bg-muted/20 animate-pulse" />}>
          <CalendarSection id="page-schedule" />
          <MultiPlatformReviews />
        </Suspense>
      )}
      
      {/* Solar Estimate Card - last section before footer on every page */}
      {showSolarEstimate && (
        <Suspense fallback={<div className="min-h-[300px] bg-muted/20 animate-pulse" />}>
          <section className="container mx-auto px-4 py-12">
            <SolarEstimateCard />
          </section>
        </Suspense>
      )}
      
      {/* Footer with contentinfo landmark */}
      <Footer />
      
      {/* Lazy load interactive widgets - not needed for initial paint */}
      {showWidgets && (
        <Suspense fallback={null}>
          <ExitIntentPopup />
          <FloatingCallButton />
          <AIChatWidget />
          <NeighborhoodActivity />
        </Suspense>
      )}
    </div>
  );
};

export default Layout;
